import React, { useState } from 'react';
import {
  InfoButton,
  IconCheckmark,
  Edit,
  ChevronDown,
  ChevronUp,
} from '@insurancemenu/imenu-components-lib/dist/icons';

import { Collapse, colors } from '@insurancemenu/imenu-components-lib';
import { CategoryStatus, FamilyMember } from 'store/modules/subscriber-imq/types';
import { MedicalConditionDelegate } from 'store/modules/subscriber-imq/medicalConditionDelegate';
import { observer } from 'mobx-react-lite';
import {
  CardHeaderStyled,
  CategoryNameButtonStyled,
  ConditionsBlockStyled,
  ConditionsInfoStyled,
  ConditionsTextStyled,
  ConditionsTitleStyled,
  EditCategoryButtonStyled,
  MedicalCategoryCardStyled,
} from './styles';
import { MedicalConditionEntry } from './MedicalConditionEntry';

interface Props {
  name: string;
  conditions: MedicalConditionDelegate[];
  status: CategoryStatus;
  editCategory: () => void;
  manageCondition: (conditionName: string, affectedMembers: FamilyMember[]) => void;
}

const ICON_MAP: Record<CategoryStatus, JSX.Element> = {
  [CategoryStatus.COMPLETED]: <IconCheckmark width="3rem" color={colors.green[600]} />,
  [CategoryStatus.DETAILS_MISSING]: <InfoButton width="3rem" color={colors.yellow[600]} />,
  [CategoryStatus.NOT_STARTED]: <InfoButton width="3rem" color={colors.red[600]} />,
};

const getChevronIcon = (showConditions: boolean): JSX.Element =>
  showConditions ? <ChevronUp width="3rem" height="3rem" /> : <ChevronDown width="3rem" height="3rem" />;

export const MedicalCategoryCard: React.FC<Props> = observer(
  ({ name, conditions, status, editCategory, manageCondition }) => {
    const [showConditions, setShowConditions] = useState(true);
    const hasConditions = conditions.length > 0;

    const handleShowConditions = (): void => {
      if (!hasConditions) {
        return;
      }
      setShowConditions(!showConditions);
    };

    return (
      <MedicalCategoryCardStyled $status={status}>
        <CardHeaderStyled>
          <CategoryNameButtonStyled
            type="text"
            leftIconProp={ICON_MAP[status]}
            {...(hasConditions ? { rightIconProp: getChevronIcon(showConditions) } : {})}
            iconSize="3rem"
            onClick={handleShowConditions}
            $hasConditions={hasConditions}
          >
            {name}
          </CategoryNameButtonStyled>
          <EditCategoryButtonStyled
            type="text"
            color="primary"
            size="small"
            uppercase
            onClick={editCategory}
            leftIconProp={<Edit width="2.4rem" height="2.4rem" />}
          >
            Edit Category
          </EditCategoryButtonStyled>
        </CardHeaderStyled>
        {(hasConditions || status === CategoryStatus.COMPLETED) && (
          <ConditionsInfoStyled>
            <ConditionsTitleStyled>Conditions:</ConditionsTitleStyled>
            <ConditionsTextStyled>
              {hasConditions ? `${conditions.length} reported` : 'None reported'}
            </ConditionsTextStyled>
          </ConditionsInfoStyled>
        )}
        <Collapse show={showConditions}>
          {conditions.length > 0 && (
            <ConditionsBlockStyled>
              {conditions.map((condition) => (
                <MedicalConditionEntry
                  key={condition.data.name}
                  conditionName={condition.data.name}
                  affectedMembers={condition.data.affectedPeople}
                  status={condition.status}
                  manageCondition={(conditionName: string, affectedMembers: FamilyMember[]): void =>
                    manageCondition(conditionName, affectedMembers)
                  }
                />
              ))}
            </ConditionsBlockStyled>
          )}
        </Collapse>
      </MedicalCategoryCardStyled>
    );
  },
);
